import type { AnalyticsPoint, AnalyticsRange } from '@/types/api'

export const RANGE_PRESETS = [
  { key: '24h', label: 'Last 24 hours' },
  { key: '7d', label: 'Last 7 days' },
  { key: '30d', label: 'Last 30 days' },
  { key: '90d', label: 'Last 90 days' },
] as const

export type RangePreset = (typeof RANGE_PRESETS)[number]['key']

export function isRangePreset(value: unknown): value is RangePreset {
  return RANGE_PRESETS.some((p) => p.key === value)
}

/** `YYYY-MM-DD` of a date in the browser's zone. */
export function localDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * API `from`/`to` for a preset. Day presets send dates, which the server reads as whole days in
 * the organization's zone; `to` is omitted (= now).
 */
export function presetRange(preset: RangePreset, now: Date = new Date()): { from: string; to?: string } {
  if (preset === '24h') return { from: new Date(now.getTime() - 24 * 3600_000).toISOString() }
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 90
  const start = new Date(now)
  start.setDate(start.getDate() - (days - 1))
  return { from: localDate(start) }
}

/** Compact duration: `3d 4h`, `2h 5m`, `45m`, `30s`. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  const s = Math.max(0, Math.round(seconds))
  const d = Math.floor(s / 86400)
  const h = Math.floor((s % 86400) / 3600)
  const m = Math.floor((s % 3600) / 60)
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
  if (m > 0) return `${m}m`
  return `${s}s`
}

export function formatPlayTime(ms: number | null | undefined): string {
  return ms === null || ms === undefined ? '—' : formatDuration(ms / 1000)
}

export function formatPercent(ratio: number | null | undefined, digits = 1): string {
  if (ratio === null || ratio === undefined) return '—'
  const pct = ratio * 100
  return `${pct >= 99.95 && pct < 100 ? '99.9' : pct.toFixed(pct === 100 || pct === 0 ? 0 : digits)}%`
}

export type UptimeTone = 'good' | 'warning' | 'critical' | 'none'

/** Uptime status for badges (always shown with its label, never color alone). */
export function uptimeTone(ratio: number | null | undefined): UptimeTone {
  if (ratio === null || ratio === undefined) return 'none'
  if (ratio >= 0.99) return 'good'
  if (ratio >= 0.9) return 'warning'
  return 'critical'
}

/** Axis label for a series point: hours in the viewer's zone, days as dates. */
export function pointLabel(point: Pick<AnalyticsPoint, 'bucket' | 'start'>, granularity: AnalyticsRange['granularity'], long = false): string {
  if (granularity === 'hour') {
    const d = new Date(point.start)
    return long
      ? d.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : d.toLocaleTimeString(undefined, { hour: 'numeric' })
  }
  const [y, m, day] = point.bucket.split('-').map(Number)
  const d = new Date(y, m - 1, day)
  return d.toLocaleDateString(undefined, long ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' } : { month: 'short', day: 'numeric' })
}

/** A round axis maximum ≥ value (1, 2, 2.5, 5 × 10ⁿ). */
export function niceMax(value: number): number {
  if (!(value > 0)) return 1
  const exp = Math.pow(10, Math.floor(Math.log10(value)))
  for (const step of [1, 2, 2.5, 5, 10]) if (value <= step * exp) return step * exp
  return 10 * exp
}

/** Which labels to show so at most `max` ticks appear (always the first and last). */
export function tickIndexes(count: number, max = 7): number[] {
  if (count <= 0) return []
  if (count <= max) return Array.from({ length: count }, (_, i) => i)
  const step = Math.ceil((count - 1) / (max - 1))
  const out: number[] = []
  for (let i = 0; i < count; i += step) out.push(i)
  if (out[out.length - 1] !== count - 1) {
    if (count - 1 - out[out.length - 1] < step / 2) out.pop()
    out.push(count - 1)
  }
  return out
}
